import { normalizeArchiveSearchQuery } from "@/features/archive/archiveSearch";
import type { ArchiveEvent } from "@/features/archive/mediators/archiveEvents";
import { createMediator, type Mediator } from "@/shared/lib/componentHierarchy";

export type ArchiveSearchState = {
  readonly draft: string;
};

export type ArchiveSearchEvent = Extract<
  ArchiveEvent,
  {
    type:
      | "archive/search-draft-changed"
      | "archive/search-query-synchronized"
      | "archive/search-submission-requested"
      | "archive/search-clear-requested"
      | "archive/search-scope-change-requested";
  }
>;

export type ArchiveSearchEffect = {
  readonly type: "dispatch";
  readonly event: Extract<
    ArchiveEvent,
    {
      type:
        | "archive/search-committed"
        | "archive/search-cleared"
        | "archive/search-scope-changed";
    }
  >;
};

export function createArchiveSearchMediator(
  query: string,
): Mediator<ArchiveSearchState, ArchiveSearchEvent, ArchiveSearchEffect> {
  return createMediator<
    ArchiveSearchState,
    ArchiveSearchEvent,
    ArchiveSearchEffect
  >({ draft: query }, (state, event) => {
    switch (event.type) {
      case "archive/search-draft-changed":
        return { status: "handled", state: { draft: event.value } };
      case "archive/search-query-synchronized":
        return { status: "handled", state: { draft: event.query } };
      case "archive/search-submission-requested": {
        const query = normalizeArchiveSearchQuery(state.draft);
        if (query) {
          return {
            status: "handled",
            state,
            effects: [
              {
                type: "dispatch",
                event: { type: "archive/search-committed", query },
              },
            ],
          };
        }
        return {
          status: "handled",
          state,
          effects: [
            { type: "dispatch", event: { type: "archive/search-cleared" } },
          ],
        };
      }
      case "archive/search-clear-requested":
        return {
          status: "handled",
          state: { draft: "" },
          effects: [
            { type: "dispatch", event: { type: "archive/search-cleared" } },
          ],
        };
      case "archive/search-scope-change-requested":
        return {
          status: "handled",
          state,
          effects: [
            {
              type: "dispatch",
              event: {
                type: "archive/search-scope-changed",
                scope: event.scope,
              },
            },
          ],
        };
    }
  });
}
